// apps/spa/src/components/reminders/ReminderTable.tsx
// REMIND-01 + REMIND-02 surface — shadcn Table with optimistic enable Switch
// + ReminderRowActions (Post Now / Edit / Delete) per row.
//
// Phase 4 (Plan 04-02) extensions:
//   - Chevron column (leftmost): click toggles ReminderHistoryPanel for that row.
//     Only one panel open at a time; clicking a different row's chevron closes the previous.
//   - LiveCountdown in Next-run column: consumes useCountdownTick(60_000) tick to recompute
//     nextRunRelative on each 60s interval while the tab is visible.
//   - For event-triggered reminders (triggerType='event'), LiveCountdown shows
//     'On every event' (all-future) or 'Event: <name> in <relative>' (specific binding).
//
// Phase 7 (Plan 07-05): AnimatePresence + motion.tr row enter/exit animations.
//   Strategy: render sorted rows in two passes inside <tbody>:
//     1. AnimatePresence block — the primary motion.tr rows (animated).
//     2. After each AnimatePresence block, if the row is expanded, a history TableRow
//        is injected as a sibling. Because AnimatePresence can only wrap motion.* as
//        direct children (Fragments can't accept refs), the history panel is rendered
//        after the AnimatePresence to avoid Fragment ref issues.
//
// Columns:
//   [Chevron] Name  Channel  Schedule  Next run  Enabled  Actions
//
// Default sort: by reminder.updatedAt DESC.

import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { motion, AnimatePresence } from 'motion/react';
import type { ChannelOption, Reminder } from '@guardian/shared';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useToggleReminder } from '@/hooks/useToggleReminder';
import { useEvents } from '@/hooks/useEvents';
import { useTimeFormat } from '@/hooks/useTimeFormat';
import { formatScheduleSummary } from '@/lib/time';
import { ReminderRowActions } from './ReminderRowActions';
import { LiveCountdown } from './LiveCountdown';
import { ReminderHistoryPanel } from './ReminderHistoryPanel';
import { AutoDisabledBadge } from './AutoDisabledBadge';

export interface ReminderTableProps {
  reminders: Reminder[];
  channels: ChannelOption[];
  onDelete: (id: string) => void;
}

function channelNameFor(
  channels: ChannelOption[],
  channelId: string,
): string {
  const ch = channels.find((c) => c.id === channelId);
  return ch ? ch.name : 'Unknown channel';
}

const rowVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, scale: 0.96 },
};

const rowTransition = { type: 'spring' as const, stiffness: 300, damping: 25 };

export function ReminderTable({
  reminders,
  channels,
  onDelete,
}: ReminderTableProps) {
  const toggle = useToggleReminder();
  const { data: events = [] } = useEvents();
  const { hour12 } = useTimeFormat();
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const sorted = useMemo(
    () => [...reminders].sort((a, b) => b.updatedAt - a.updatedAt),
    [reminders],
  );

  function handleChevronClick(rowId: string) {
    setExpandedRowId((prev) => (prev === rowId ? null : rowId));
  }

  const colCount = 7; // chevron + name + channel + schedule + next-run + enabled + actions

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" aria-label="Expand history" />
          <TableHead>Name</TableHead>
          <TableHead>Channel</TableHead>
          <TableHead>Schedule</TableHead>
          <TableHead>Next run</TableHead>
          <TableHead>Enabled</TableHead>
          <TableHead aria-label="Row actions" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {/* Render each row as a motion.tr + optional history panel sibling.
            AnimatePresence is scoped per-row to avoid the Fragment-ref limitation:
            each motion.tr is a direct child of its own AnimatePresence, so the ref
            injection works correctly. The history panel TableRow is always a sibling
            of the AnimatePresence, never inside it. */}
        {sorted.map((r) => {
          const channelName = channelNameFor(channels, r.channelId);
          const isExpanded = expandedRowId === r.id;

          return (
            <>
              <AnimatePresence key={r.id} mode="popLayout" initial={false}>
                <motion.tr
                  key={r.id}
                  layout
                  variants={rowVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={rowTransition}
                  data-paused={!r.enabled ? 'true' : undefined}
                  className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
                >
                  {/* Chevron toggle */}
                  <TableCell className="pr-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} history for ${r.name}`}
                      aria-expanded={isExpanded}
                      onClick={() => handleChevronClick(r.id)}
                      data-testid={`chevron-${r.id}`}
                    >
                      <span aria-hidden="true">{isExpanded ? '▼' : '▶'}</span>
                    </Button>
                  </TableCell>

                  <TableCell>
                    <Link
                      to={`/reminders/${r.id}/edit`}
                      className={cn(
                        'font-medium hover:underline',
                        !r.enabled && 'text-muted-foreground',
                      )}
                    >
                      {r.name}
                    </Link>
                  </TableCell>
                  <TableCell>#{channelName}</TableCell>
                  <TableCell>{formatScheduleSummary(r.scheduleCron, hour12)}</TableCell>
                  <TableCell>
                    <LiveCountdown reminder={r} events={events} />
                  </TableCell>
                  <TableCell>
                    {r.disabledReason === 'auto_failure' ? (
                      <AutoDisabledBadge reminder={r} />
                    ) : (
                      <Switch
                        checked={r.enabled}
                        onCheckedChange={(enabled) =>
                          toggle.mutate({ id: r.id, enabled })
                        }
                        aria-label={`Toggle ${r.name}`}
                      />
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <ReminderRowActions
                      reminder={r}
                      channelName={channelName}
                      onDelete={onDelete}
                    />
                  </TableCell>
                </motion.tr>
              </AnimatePresence>

              {/* Expanded history panel — sibling of AnimatePresence, not inside it */}
              {isExpanded && (
                <TableRow key={`${r.id}-history`} data-testid={`history-panel-${r.id}`}>
                  <TableCell colSpan={colCount} className="p-0 bg-muted/30">
                    <ReminderHistoryPanel reminderId={r.id} />
                  </TableCell>
                </TableRow>
              )}
            </>
          );
        })}
      </TableBody>
    </Table>
  );
}
